import React, { useEffect, useState } from 'react';
import {
  Search, Plus, Pencil, Trash2, Eye, Shield, ShieldOff, Mail, Phone, Globe,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getUsers, addUser, updateUser, deleteUser,
} from '../firebase/firestore';
import AdminModal, { ModalFooter, FormInput, FormSelect, Toggle } from '../components/AdminModal';

const EMPTY_USER = {
  uid: '',
  fullName: '',
  email: '',
  phone: '',
  country: '',
  countryCode: '',
  photoUrl: '',
  verified: false,
  blocked: false,
  role: 'user',
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | 'add' | user object | { view: user }
  const [form, setForm] = useState(EMPTY_USER);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getUsers()
      .then(d => { setUsers(d); setLoading(false); })
      .catch(e => { toast.error(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY_USER); setModal('add'); };
  const openEdit = u => {
    setForm({
      uid: u.uid || u.id,
      fullName: u.fullName || '',
      email: u.email || '',
      phone: u.phone || '',
      country: u.country || '',
      countryCode: u.countryCode || '',
      photoUrl: u.photoUrl || '',
      verified: !!u.verified,
      blocked: !!u.blocked,
      role: u.role || 'user',
    });
    setModal(u);
  };
  const openView = u => setModal({ view: u });
  const close = () => setModal(null);

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const save = async () => {
    if (!form.fullName?.trim() && !form.email?.trim()) {
      toast.error('Name or email is required');
      return;
    }
    setSaving(true);
    try {
      if (modal === 'add') {
        await addUser(form);
        toast.success('User profile created');
      } else {
        await updateUser(modal.id, {
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          country: form.country.trim(),
          countryCode: form.countryCode.trim(),
          photoUrl: form.photoUrl.trim(),
          verified: form.verified,
          blocked: form.blocked,
          role: form.role,
        });
        toast.success('User updated');
      }
      close();
      load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async u => {
    if (!window.confirm(`Delete user "${u.fullName || u.email}"?\nThis removes their Firestore profile and tickets subcollection.`)) return;
    try {
      await deleteUser(u.id);
      toast.success('User deleted');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggleBlock = async u => {
    try {
      await updateUser(u.id, { blocked: !u.blocked });
      toast.success(u.blocked ? 'User unblocked' : 'User blocked');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const formatDate = raw => {
    if (!raw) return '—';
    try {
      const d = raw.toDate ? raw.toDate() : new Date(raw);
      return d.toLocaleDateString();
    } catch {
      return '—';
    }
  };

  const filtered = users.filter(u =>
    `${u.fullName} ${u.email} ${u.phone} ${u.id}`.toLowerCase().includes(search.toLowerCase())
  );

  const isView = modal && modal.view;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-white font-bold text-xl">Users</h2>
          <p className="text-[#546E7A] text-sm">{users.length} registered users · full CRUD</p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#2D5BE3] hover:bg-[#1A3C8F]
            text-white rounded-xl text-sm font-semibold transition-colors"
        >
          <Plus size={16} /> Add User
        </button>
      </div>

      <div className="bg-[#2D5BE3]/10 border border-[#2D5BE3]/25 rounded-xl p-3 text-xs text-[#B0BEC5]">
        Manage Firestore user profiles. <strong className="text-white">Add User</strong> creates a profile record;
        link an existing Firebase Auth UID in the UID field. Deleting removes profile data only (not Auth account).
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#546E7A]" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, phone, ID..."
          className="w-full pl-9 pr-4 py-2.5 bg-[#1A2235] border border-[#1E2D45] rounded-xl
            text-white text-sm placeholder-[#546E7A] focus:outline-none focus:border-[#2D5BE3]"
        />
      </div>

      <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-[#1E2D45]">
              {['User', 'Contact', 'Country', 'Joined', 'Verified', 'Status', 'Actions'].map(h => (
                <th key={h} className="text-left text-xs text-[#546E7A] font-semibold px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-[#546E7A]">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-[#546E7A]">No users found</td></tr>
            ) : filtered.map(u => (
              <tr key={u.id} className="border-b border-[#1E2D45] hover:bg-white/[0.02] transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {u.photoUrl ? (
                      <img src={u.photoUrl} alt="" className="w-9 h-9 rounded-xl object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-xl bg-[#2D5BE3]/20 flex items-center justify-center
                        text-[#2D5BE3] font-bold text-sm">
                        {(u.fullName || u.email || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="text-white text-sm font-medium">{u.fullName || '—'}</div>
                      <div className="text-[#546E7A] text-xs font-mono">{u.id.slice(0, 12)}…</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 text-[#B0BEC5] text-xs mb-1">
                    <Mail size={11} /> {u.email || '—'}
                  </div>
                  <div className="flex items-center gap-1 text-[#B0BEC5] text-xs">
                    <Phone size={11} /> {u.phone || '—'}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 text-[#B0BEC5] text-xs">
                    <Globe size={11} /> {u.country || '—'}
                  </div>
                </td>
                <td className="px-4 py-3 text-[#B0BEC5] text-xs">{formatDate(u.createdAt)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold
                    ${u.verified ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                    {u.verified ? 'Verified' : 'Unverified'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold
                    ${u.blocked ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400'}`}>
                    {u.blocked ? 'Blocked' : 'Active'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    <button onClick={() => openView(u)} title="View"
                      className="w-8 h-8 rounded-lg bg-[#1E2D45] hover:bg-white/10 flex items-center justify-center text-[#B0BEC5] hover:text-white">
                      <Eye size={14} />
                    </button>
                    <button onClick={() => openEdit(u)} title="Edit"
                      className="w-8 h-8 rounded-lg bg-[#2D5BE3]/10 hover:bg-[#2D5BE3]/20 flex items-center justify-center text-[#2D5BE3]">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => toggleBlock(u)} title={u.blocked ? 'Unblock' : 'Block'}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors
                        ${u.blocked ? 'bg-green-500/10 text-green-400' : 'bg-orange-500/10 text-orange-400'}`}>
                      {u.blocked ? <Shield size={14} /> : <ShieldOff size={14} />}
                    </button>
                    <button onClick={() => remove(u)} title="Delete"
                      className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && isView && (
        <AdminModal title="User Details" onClose={close} wide>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {modal.view.photoUrl ? (
                <img src={modal.view.photoUrl} alt="" className="w-16 h-16 rounded-2xl object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-[#2D5BE3]/20 flex items-center justify-center text-2xl font-bold text-[#2D5BE3]">
                  {(modal.view.fullName || '?')[0]}
                </div>
              )}
              <div>
                <div className="text-white font-bold text-lg">{modal.view.fullName || '—'}</div>
                <div className="text-[#546E7A] text-xs font-mono">{modal.view.id}</div>
              </div>
            </div>
            {[
              ['Email', modal.view.email],
              ['Phone', modal.view.phone],
              ['Country', modal.view.country],
              ['Role', modal.view.role || 'user'],
              ['Verified', modal.view.verified ? 'Yes' : 'No'],
              ['Blocked', modal.view.blocked ? 'Yes' : 'No'],
              ['Joined', formatDate(modal.view.createdAt)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-2 border-b border-[#1E2D45]/50">
                <span className="text-[#546E7A] text-sm">{k}</span>
                <span className="text-white text-sm">{v || '—'}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={() => { close(); openEdit(modal.view); }}
              className="flex-1 py-2.5 bg-[#2D5BE3] text-white rounded-xl text-sm font-semibold">
              Edit User
            </button>
            <button onClick={close}
              className="flex-1 py-2.5 bg-[#1A2235] border border-[#1E2D45] text-[#B0BEC5] rounded-xl text-sm">
              Close
            </button>
          </div>
        </AdminModal>
      )}

      {modal && !isView && (
        <AdminModal
          title={modal === 'add' ? 'Add User Profile' : 'Edit User'}
          onClose={close}
          wide
          footer={
            <ModalFooter
              onCancel={close}
              onSave={save}
              saving={saving}
              saveLabel={modal === 'add' ? 'Create User' : 'Save Changes'}
            />
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {modal === 'add' && (
              <FormInput
                label="Firebase UID (optional)"
                value={form.uid}
                onChange={v => f('uid', v)}
                placeholder="Leave empty to auto-generate doc ID"
                className="sm:col-span-2"
              />
            )}
            <FormInput label="Full Name" value={form.fullName} onChange={v => f('fullName', v)} required />
            <FormInput label="Email" value={form.email} onChange={v => f('email', v)} type="email" />
            <FormInput label="Phone" value={form.phone} onChange={v => f('phone', v)} />
            <FormInput label="Country" value={form.country} onChange={v => f('country', v)} />
            <FormInput label="Country Code" value={form.countryCode} onChange={v => f('countryCode', v)} placeholder="LK" />
            <FormInput
              label="Photo URL"
              value={form.photoUrl}
              onChange={v => f('photoUrl', v)}
              className="sm:col-span-2"
            />
            <FormSelect
              label="Role"
              value={form.role}
              onChange={v => f('role', v)}
              options={[
                { value: 'user', label: 'User' },
                { value: 'admin', label: 'Admin' },
              ]}
            />
            <div className="sm:col-span-2 flex flex-wrap gap-6 pt-2">
              <Toggle label="Verified" checked={form.verified} onChange={v => f('verified', v)} color="#00C853" />
              <Toggle label="Blocked" checked={form.blocked} onChange={v => f('blocked', v)} color="#FF1744" />
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
}
